import {useDispatch} from 'react-redux'
import {useState,useRef,useEffect,useCallback} from 'react'
import { Line,Scatter } from 'react-chartjs-2'
import HowTo from './HowTo'
import Webcam from "react-webcam"
import useCountDown from "react-countdown-hook"
import {capitalize} from '../../util/util'
import useCurrentUser from '../../hooks/useCurrentUser'
import useWindowSize from '../../hooks/useWindowSize'
import * as poseDetection from '@tensorflow-models/pose-detection'
import * as tf from '@tensorflow/tfjs-core'
// Register one of the TF.js backends.
import '@tensorflow/tfjs-backend-webgl'
import {
	isWebview,
	measureShoulderDistance,
	sideKneeAngle,
	confidentSide,
	normalizeX,
	repConsistency,
	tempoConsistency,
	depthScore,
	extractRepSlices,
	nonMonotonicIndexes,
	argMin,
	argMax,
	getKneeCaveTimeMap,
	restConsistency,
	kneeArticulationConsistency,
	centerLineConsistency,
	repsCenterLineScore,
	chestCenter,
	hipCenter,
	useAnimationFrame,
	poseFemurLength,
	poseHeight,
	balancingSide,
	calculateCenterStatus,
	calculateShoulderTilt,
	calculateHipTilt,
	tiltScore,
	kneeHeightScore,
	calculateThighLength,
	calculateCalfLength,
	heelHipDistanceEstimate,
	nearest
} from './util'
import {
	removeLeaderboardRecord,
	editLeaderboardRecord,
	addLeaderboardRecord,
	setMetricCreator,
	setPoses,
	setShow
} from './slice'
import styles from './AIDemo.module.css'
import Guides from './Guides'
import {dateToYMD} from '../../util/datetime'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Table from 'react-bootstrap/Table'
import Spinner from 'react-bootstrap/Spinner'
import Leaderboard from './Leaderboard'
import Poses from './Poses'

const SCORE_THRESHOLD = 0.3
//320 is the max that the modal can input is within a 320x320 box T.T
const MODEL_INPUT_SIZE = 512
const ROTATION_ANGLE_THRESHOLD = 30
const RESET_ANGLE_THRESHOLD = 18

const detectorConfig = {
	modelType: poseDetection.movenet.modelType.SINGLEPOSE_THUNDER,
	enableTracking: true,
	trackerType: poseDetection.TrackerType.BoundingBox,
	multiPoseMaxDimension:MODEL_INPUT_SIZE
}

const RotationDemo = ()=>{
	const dispatch = useDispatch()
	const [windowWidth,windowHeight] = useWindowSize()

	const user = useCurrentUser()
	const isClient = user.account_type === 'client'
	const ratio = 1//windowWidth/windowHeight

	const [detector,setDetector] = useState(null)
	const [showHowTo,setShowHowTo] = useState(false)
	const [showResults,setShowResults] = useState(false)
	const [showLeaderboard,setShowLeaderboard] = useState(false)
	const testVidRef = useRef(null)
	const testVidLoadedRef = useRef(false)

	const canvasRef = useRef(null)

	const leftDegreeSpanRef = useRef(null)

	const rightDegreeSpanRef = useRef(null)

	const refMap = {
		left:leftDegreeSpanRef,
		right:rightDegreeSpanRef
	}

	//stores results from squat analysis
	const emptyResults = ()=>({
		left:0,
		right:0,
	})

	const [results,setResults] = useState(emptyResults())//useState(testSingleLegData)
	const resultsRef = useRef(emptyResults())

	const [timeLeft, {start:startCoundown,reset:resetCountdown}] = useCountDown(3000, 1000)
	const countingDownRef = useRef(false)

	useEffect(()=>{
		if(!overlayRef.current||!countingDownRef.current) return
		if(timeLeft===0&&countingDownRef.current){
			countingDownRef.current = false
			toggleIsReady()
			setTemporaryOverlay('GO!',1000)
		}else{
			console.log(timeLeft)
			overlayRef.current.innerText = Math.ceil(timeLeft/1000)
		}
	},[timeLeft,countingDownRef])

	//console.log(results)

	const posesRef = useRef([])
	const [showGuides,setShowGuides] = useState(false)
	const [showOverlay,setShowOverlay] = useState(false)
	const overlayRef = useRef(null)
	const guidesRef = useRef({
		shoulderWidth:0
	})

	const [name,setName] = useState('')
	const [email,setEmail] = useState('')

	useEffect( async ()=>{
		setDetector(
			await poseDetection.createDetector(
				poseDetection.SupportedModels.MoveNet,
				detectorConfig
			)
		)
	},[])

	//console.log('render')
	//reference to if we have initialized the squat guides
	const initializedRef = useRef(false)
	const trackResultsRef = useRef(false)
	const webcamRef = useRef(null)

	const stateDisplayTextMap = {
		waiting:'Waiting',
		setup:'Get Ready',
		measure:'Measuring'
	}

	const initState = 'waiting'
	const stateRef = useRef(initState)
	const stateSpanRef = useRef(null)
	const sideRef = useRef('right')
	const isReadyRef = useRef(false)

	const measuringRef = useRef(false)

	const setTemporaryOverlay = (text,ttl)=>{
		overlayRef.current.innerText = text
		setShowOverlay(true)
		setTimeout(()=>{
			setShowOverlay(false)
		},ttl)
	}

	//helper functions used during inferencing
	const isMeasuring = () => measuringRef.current
	const isReady = () => isReadyRef.current
	const toggleMeasuring = () => measuringRef.current = !measuringRef.current
	const isCountingDown = ()=> countingDownRef.current

	const getSide = () => sideRef.current
	const setSide = side => { sideRef.current = side }
	const toggleIsReady = () => { isReadyRef.current = !isReadyRef.current }

	const setShoulderWidth = w => guidesRef.current.shoulderWidth = w
	const getShoulderWidth = () => guidesRef.current.shoulderWidth

	const getAngle = (shoulderWidth,measuredShoulderWidth)=>(
		Math.acos(measuredShoulderWidth/shoulderWidth)/Math.PI*180
	)

	const setSideMaxAngle = (side,angle)=>{
		resultsRef.current[side] = Math.max(
			resultsRef.current[side],
			angle
		)
	}

	const getSideAngle = (side)=>resultsRef.current[side]

	const displaySideAngle = (side)=>{
		refMap[side].current.innerText = getSideAngle(side).toFixed(1)
	}

	//predicates
	const isReadyPose = keypoints => {
		const horizontalTolerance = measureShoulderDistance(keypoints)/4.5
		const verticalTolerance = measureShoulderDistance(keypoints)/5.5
		const leftShoulder = keypoints[5]
		const leftElbow = keypoints[7]
		const leftWrist = keypoints[9]

		const leftArmInPosition = (
			//arm not overhead
			leftElbow.y > leftShoulder.y &&
			leftWrist.y > leftShoulder.y &&
			//arms tucked in
			Math.abs(leftWrist.y - leftElbow.y) < verticalTolerance &&
			Math.abs(leftShoulder.x - leftWrist.x) < horizontalTolerance &&
			Math.abs(leftShoulder.x - leftElbow.x) < horizontalTolerance
		)

		const rightShoulder = keypoints[6]
		const rightElbow = keypoints[8]
		const rightWrist = keypoints[10]

		const rightArmInPosition = (
			//arm not overhead
			rightElbow.y > rightShoulder.y &&
			rightWrist.y > rightShoulder.y &&
			//arms tucked in
			Math.abs(rightWrist.y - rightElbow.y) < verticalTolerance &&
			Math.abs(rightShoulder.x - rightWrist.x) < horizontalTolerance &&
			Math.abs(rightShoulder.x - rightElbow.x) < horizontalTolerance
		)

		return leftArmInPosition && rightArmInPosition
	}

	const hasResult = side => resultsRef.current[side] > ROTATION_ANGLE_THRESHOLD

	const hasAllResults = () => hasResult('left') && hasResult('right')

	const infer = useCallback( async () => {
		if ((!webcamRef.current&&!testVidRef.current)||!detector) {
			//console.log('Waiting for dependencies')
		} else {
			const video = webcamRef.current?.video || testVidRef.current

			const ctx = canvasRef.current.getContext("2d")
			ctx.drawImage(video,0,0,MODEL_INPUT_SIZE,MODEL_INPUT_SIZE/ratio)
			if (!initializedRef.current||!trackResultsRef.current) {
				console.log('Not initialized')
				return
			}

			try {
				//console.log(`inferencing`)
				const poses = await detector.estimatePoses(canvasRef.current)
				if ( poses.length > 0 ) {
					const t = Date.now()
					const {keypoints} = poses[0]
					if (!isReady()&&isReadyPose(keypoints)&&!isCountingDown()) {
						setShoulderWidth(measureShoulderDistance(keypoints))
						setShowOverlay(true)
						startCoundown()
						countingDownRef.current = true
					}
					if (isReady()&&!isMeasuring()) {
						const side = getSide()
						toggleMeasuring()
						setTemporaryOverlay(`Turn ${side==='right'?'Left':'Right'}`,1500)
					}
					if (isMeasuring()) {
						const side = getSide()
						const angle = getAngle(
							getShoulderWidth(),
							measureShoulderDistance(keypoints)
						)
						if (angle>ROTATION_ANGLE_THRESHOLD) {
							displaySideAngle(side)
							setSideMaxAngle(side,angle)
						}
						if(angle<RESET_ANGLE_THRESHOLD){
							if(hasAllResults()){
								setShowOverlay(true)
								overlayRef.current.innerText = 'Done'
								setTimeout(()=>{
									setShowOverlay(false)
									toggleMeasuring()
									setShowResults(true)
									cancelInferencing()
								},2000)
							} else if(hasResult('right')) {
								setSide('left')
								toggleMeasuring()
							}
						}
					}
				}

			} catch(err) {
				console.error(err)
			}
		}
	},[detector,webcamRef,guidesRef])

	const {
		cancel:cancelInferencing,
		start:startInferencing
	} = useAnimationFrame(infer)

	const [started,setStarted] = useState(false)

	const startDemo = async ()=>{
		setStarted(true)
		cancelInferencing()
		setShowGuides(false)
		isReadyRef.current = false
		measuringRef.current = false
		sideRef.current = 'right'
		resultsRef.current = emptyResults()
		stateSpanRef.current.innerText = stateDisplayTextMap['setup']

		initializedRef.current = true
		stateRef.current = 'setup'
		resultsRef.current = emptyResults()
		trackResultsRef.current = true
		if (testVidRef.current) testVidRef.current.play()
		startInferencing()
		//run a single inference to load model unto GPU
		const ctx = canvasRef.current.getContext("2d")
		if (testVidRef.current) {
			ctx.drawImage(testVidRef.current,0,0,MODEL_INPUT_SIZE,MODEL_INPUT_SIZE/ratio)
		} else {
			ctx.drawImage(webcamRef.current.video,0,0,MODEL_INPUT_SIZE,MODEL_INPUT_SIZE/ratio)
		}
		const poses = await detector.estimatePoses(canvasRef.current)
	}

	const restartDemo = ()=>{
		setShowResults(false)
		setShowGuides(false)
		cancelInferencing()
		setStarted(false)
		resultsRef.current = emptyResults()
		measuringRef.current = false
		isReadyRef.current = false
	}

	const stopDemo = ()=>{
		setShowOverlay(false)
		setShowGuides(false)
		cancelInferencing()
		setStarted(false)
		refMap.left.current.innerText = 0
		refMap.right.current.innerText = 0
		resultsRef.current = emptyResults()
		measuringRef.current = false
		isReadyRef.current = false
		stateSpanRef.current.innerText = stateDisplayTextMap[initState]
		if(testVidRef.current){
			testVidRef.current.pause()
			testVidRef.current.currentTime = 0
		}
	}

	const handleStartTracking = ()=>{
		resultsRef.current = emptyResults()
		trackResultsRef.current=true
		startInferencing()
	}

	const handleReset = ()=>{
		cancelInferencing()
	}

	const [saving,setSaving] = useState(false)

	const handleSave = ()=>{
		setSaving(true)
		dispatch(addLeaderboardRecord({
			contact_email:email,
			rep_pace:repConsistency(results.reps),
			rest_pace:restConsistency(results.reps),
			depth_score:depthScore(results.reps,results.chestPosition,guidesRef.current.bottomY,guidesRef.current.topY),
			center_balance:centerLineConsistency(results.chestPosition),
			left_knee_balance:kneeArticulationConsistency(
				results.reps,
				results.leftKneePosition,
				results.kneeCaveMap.left
			),
			right_knee_balance:kneeArticulationConsistency(
				results.reps,
				results.rightKneePosition,
				results.kneeCaveMap.right
			),
			name:name,
		})).then(()=>{
			setSaving(false)
			setName('')
			setEmail('')
		})
	}

	const handleAddToProgress = ()=>{
		const metrics = [
			{
				name:`Left Rotation`,
				description:'',
				//implicit
				unit_id:1,
				value:resultsRef.current.left
			},{
				name:`Right Rotation`,
				description:'',
				//implicit
				unit_id:1,
				value:resultsRef.current.right
			},{
				name:`Rotation Difference`,
				description:'',
				//implicit
				unit_id:1,
				value:Math.abs(resultsRef.current.right - resultsRef.current.left)
			}
		]
		dispatch(setMetricCreator({
			show:true,
			category:'AI Seated Rotational Twist',
			date:dateToYMD(new Date()),
			metrics
		}))
	}

	return (
		<div className={styles.AIDemoWidget}>
			<div ref={overlayRef} style={{
				position:'absolute',
				width:'100vw',
				height:'100vh',
				background:'rgba(0,0,0,0.4)',
				color:'rgb(50,255,50)',
				display:showOverlay?'flex':'none',
				justifyContent:'center',
				fontWeight:600,
				alignItems:'center',
				fontSize:'110px',
				textAlign:'center',
				zIndex:3
			}}/>
			{!showResults&&
				<div className="m-auto">
					<div style={{position:'relative'}}>
						
						<svg style={{
							position:'absolute',
							top:0,left:0,
							zIndex:2,
							overflow:'visible',
							height:`${Math.min(windowWidth,windowHeight)}px`,
							width:`${Math.min(windowWidth,windowHeight)}px`,
						}}>
							
						</svg>
					</div>
					
					{/*<video 
						style={{
							height:`${Math.min(windowWidth,windowHeight)}px`,
							width:`${Math.min(windowWidth,windowHeight)}px`,
							display:'block'
						}}
						onLoadedData={()=>{testVidLoadedRef.current = true}}
						crossorigin='anonymous' 
						ref={testVidRef} 
						muted loop
						src="http://localhost/assets/videos/flexion-crop.mp4"
					/>*/}
					<Webcam 
						style={{
							height:windowWidth>windowHeight?'100vh':'auto',
							width:windowWidth>=windowHeight?'auto':'100vw',
							display:'block',
							margin:'auto'
						}}
						ref={webcamRef}
						mirrored
						videoConstraints={{
							width: 720,
							height:720,
							facingMode:"user",
							deviceId:"c141e16a643707300abde9eaae8a522f531793da15be147c3e1d7f40dcdfaea7"
						}}
					/>
					<canvas 
						style={{display:'none'}} 
						width={MODEL_INPUT_SIZE} height={MODEL_INPUT_SIZE} 
						ref={canvasRef}
					/>
					<div style={{
						position:'absolute',
						top:'4px',
						right:'4px',
						background:'rgba(255,255,255,0.9)',
						padding:'8px',
						borderRadius:'8px',
						zIndex:3,
						width:'200px'
					}}>
						<div className="d-flex align-items-stretch justify-content-around mb-2" style={{fontSize:'24px'}}>
							<div>
								<div>
									<span 
										style={{width:'60px',textAlign:'right',display:'inline-block'}} 
										ref={leftDegreeSpanRef}>0</span>&#xb0;
								</div>
								<small style={{fontSize:'14px',margin:0}} className="text-muted d-block text-center">Left</small>
							</div>
							<span style={{background:'#ccc',width:'1px'}}/>
							<div>
								<div>
									<span 
										style={{width:'60px',textAlign:'right',display:'inline-block'}} 
										ref={rightDegreeSpanRef}>0</span>&#xb0;
								</div>
								<small style={{fontSize:'14px',margin:0}} className="text-muted d-block text-center">Right</small>
							</div>
						</div>
						<div className="d-flex justify-content-between">
							<small className="mr-2" ref={stateSpanRef}>Waiting</small>
							<small className={Boolean(detector)?'text-success':'text-muted'}>
								{Boolean(detector)?'AI Downloaded':'Downloading AI...'}
							</small>
						</div>
					</div>
					<div style={{
						position:'absolute',
						bottom:0,
						left:0,
						display:'flex',
						flexWrap:'wrap',
						background:'rgba(255,255,255,0.9)',
						padding:'8px',
						zIndex:3
					}}>
						<Button
							className="mr-2"
							variant="outline-primary"
							onClick={()=>{
								setShowHowTo(p=>!p)
							}}
						>
							<i className="fa fa-question-circle" /> How To
						</Button>
						{!started&&
							<Button className="mr-2" onClick={startDemo} variant="outline-success">
								Start
							</Button>
						}
						{started&&
							<Button className="mr-2" onClick={stopDemo} variant="outline-danger">
								Stop
							</Button>
						}
						{Boolean(testVidRef.current)&&
							<Button className="mr-2" onClick={()=>{
								testVidRef.current.pause()
							}} variant="outline-info">
								Pause
							</Button>
						}
						{!isWebview()&&
							<Button 
								className="mr-2"
								onClick={()=>{
									dispatch(setShow(false))
								}}
								variant="outline-danger" 
							>
								Close
							</Button>
						}
					</div>
				</div>
			}
			{showResults&&
				<div style={{
					width:'100vw',
					height:'100vh',
					overflow:'auto',
					padding:'16px'
				}}>
					<div style={{
						fontSize:'32px',
						textAlign:'center',
						marginTop:'44px',
						marginBottom:'16px'
					}}>
						Results Analysis
					</div>
					<Table striped bordered>
						<thead>
							<tr>
								<th>Metric</th>
								<th>Value</th>
							</tr>
						</thead>
						<tbody>
							<tr>
								<td>Left Rotation</td>
								<td>{resultsRef.current.left.toFixed(1)}</td>
							</tr>
							<tr>
								<td>Right Rotation</td>
								<td>{resultsRef.current.right.toFixed(1)}</td>
							</tr>
							<tr>
								<td>Difference</td>
								<td>{Math.abs(resultsRef.current.right - resultsRef.current.left).toFixed(1)}</td>
							</tr>
						</tbody>
					</Table>
					<Button 
						onClick={handleAddToProgress}
						variant="outline-success" 
						className="my-3"
						block
					>
						Add to {isClient?'':"Client's"} Progress Metrics
					</Button>
					<div className="mt-5 d-flex">
						<Button 
							className="mr-2"
							onClick={restartDemo}
							variant="outline-dark"
						>
							Restart
						</Button>
						{!isWebview()&&
							<Button 
								onClick={()=>{
									dispatch(setShow(false))
								}}
								variant="outline-danger" 
							>
								Close
							</Button>
						}
					</div>
				</div>
			}
			{(parseInt(user.user_id_number) === 100017099 || window.location.hostname === 'localhost')&&
				<div style={{
					position:'absolute',
					top:0,left:0,
					transform:`translateX(${showLeaderboard?'0':'-100%'})`, 
					transition:'0.2s',
					width:'600px',
					maxWidth:'100%',
					height:'100vh',
					zIndex:6,
					boxShadow:showLeaderboard?'0 0 16px rgba(0,0,0,0.6)':'none'
				}}>
					<button 
						onClick={()=>{
							setShowLeaderboard( p => !p )
						}}
						style={{
							position:'absolute',
							top:'80px',right:0,
							transform:'translateX(100%)',
							padding:'16px',
							background:'#fff',
							border:'none',
							zIndex:5,
							boxShadow:'0 0 16px rgba(0,0,0,0.2)',
						}}
					>
						Leaderboard
					</button>
					<div style={{
						width:'100%',
						height:'100%',
						overflow:'auto',
						position:'absolute',
						background:'#fff',
						padding:'8px',
						top:0,left:0,
						zIndex:6
					}}>
						<Leaderboard/>
					</div>
				</div>
			}
			{showHowTo&&
				<HowTo onHide={
					()=>{setShowHowTo(false)}
				}>
					<strong>What is this?</strong>
					<p>
						With only a webcam, our system will automatically measure seated spinal rotation angle
					</p>
					<strong>How do I use this?</strong>
					<ul>
						<li><span className="mr-2">1.</span>Press the green <span id={styles.StartHowTo}>Start</span> button & <strong>wait</strong> till it turns into a red <span id={styles.StopHowTo}>Stop</span> button (the first time always takes a few seconds)</li>
						<li><span className="mr-2">2.</span>Sit down facing camera directly.</li>
						<li><span className="mr-2">3.</span>Place dowel behind the back holding between your elbows.</li>
						<li><span className="mr-2">4.</span>With control, rotate as far as possible to each side.</li>
					</ul>
					<div className="d-flex align-items-end flex-wrap justify-content-around">
						<a target="_blank" href="https://www.youtube.com/watch?v=rPjhkwmExEY">Check out a live demo</a>
						<div className="text-center">
							<div><small>For more more info read the</small></div>
							<a target="_blank" href="https://www.fitsw.com/blog/exercise-motion-analysis-ai/">Blog Post About fitsw.ai</a>
						</div>
					</div>
				</HowTo>
			}
		</div>
	)
}

export default RotationDemo